// api.ts - API client for NOVA widget

import type { NovaSession, NovaMessage, NovaError } from './types';
import type { UsageStatus } from './auth';

export interface InitResponse {
  success: boolean;
  sessionId: string;
  isNew: boolean;
  siteId: string;
  siteName?: string;
  linkedUser?: {
    userId: string;
    platform: string;
  } | null;
}

export interface MessageResponse {
  success: boolean;
  response?: string;
  messages?: string[];
  type?: 'single' | 'multi';
  error?: string;
  message?: string;
  retryAfterMs?: number;
  /** false when the reply did not spend the daily quota (clarify, brief, error notices) */
  billable?: boolean;
  usage?: UsageStatus | null;
  /** Follow-up questions to offer as chips under the answer */
  suggestions?: string[];
}

export interface HistoryResponse {
  success: boolean;
  messages: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
    userId?: string;
  }>;
  error?: string;
}

export class NovaAPIClient {
  private apiKey: string;
  private apiUrl: string;
  private sessionId: string | null = null;

  constructor(apiKey: string, apiUrl: string = 'https://nova.zeroauthoritydao.com') {
    this.apiKey = apiKey;
    this.apiUrl = apiUrl.replace(/\/$/, ''); // Remove trailing slash
  }

  private async fetch<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.apiUrl}/api/widget${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-api-key': this.apiKey,
      ...(this.sessionId && { 'x-session-id': this.sessionId }),
      ...(options.headers as Record<string, string>),
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      const error: NovaError = {
        code: data.error || 'unknown_error',
        message: data.message || 'An unexpected error occurred',
        retryAfterMs: data.retryAfterMs,
      };
      throw error;
    }

    return data as T;
  }

  /**
   * Initialize a widget session
   */
  async initSession(options: {
    visitorId?: string;
    metadata?: Record<string, unknown>;
    existingSessionId?: string;
  } = {}): Promise<NovaSession> {
    // Try to resume existing session if provided
    if (options.existingSessionId) {
      this.sessionId = options.existingSessionId;
    }

    const response = await this.fetch<InitResponse>('/init', {
      method: 'POST',
      body: JSON.stringify({
        visitorId: options.visitorId,
        metadata: {
          ...options.metadata,
          pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
          referrer: typeof document !== 'undefined' ? document.referrer : undefined,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        },
      }),
    });

    this.sessionId = response.sessionId;

    return {
      sessionId: response.sessionId,
      siteId: response.siteId,
      siteName: response.siteName,
      isNew: response.isNew,
      linkedUser: response.linkedUser as NovaSession['linkedUser'],
    };
  }

  /**
   * Send a message and get a response
   */
  async sendMessage(
    content: string,
    metadata?: Record<string, unknown>
  ): Promise<{ response: string; messages: string[]; billable?: boolean; suggestions: string[]; usage?: UsageStatus | null }> {
    if (!this.sessionId) {
      throw {
        code: 'no_session',
        message: 'Session not initialized. Call initSession() first.',
      } as NovaError;
    }

    const response = await this.fetch<MessageResponse>('/message', {
      method: 'POST',
      body: JSON.stringify({
        content,
        metadata,
      }),
    });

    if (!response.success) {
      throw {
        code: response.error || 'message_failed',
        message: response.message || 'Failed to send message',
        retryAfterMs: response.retryAfterMs,
      } as NovaError;
    }

    return {
      response: response.response || '',
      messages: response.messages || [response.response || ''],
      billable: response.billable,
      suggestions: response.suggestions || [],
      usage: response.usage ?? null,
    };
  }

  /**
   * Get conversation history
   */
  async getHistory(limit: number = 20): Promise<NovaMessage[]> {
    if (!this.sessionId) {
      return [];
    }

    try {
      const response = await this.fetch<HistoryResponse>(
        `/message/history?limit=${limit}`,
        { method: 'GET' }
      );

      if (!response.success) {
        return [];
      }

      return response.messages.map((msg, index) => ({
        id: `msg_${index}_${new Date(msg.timestamp).getTime()}`,
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.timestamp),
        feedback: null,
      }));
    } catch {
      return [];
    }
  }

  /**
   * Submit feedback for a message
   */
  async submitFeedback(
    messageId: string,
    feedbackType: 'thumbs_up' | 'thumbs_down',
    feedbackText?: string
  ): Promise<boolean> {
    if (!this.sessionId) {
      return false;
    }

    try {
      await this.fetch('/message/feedback', {
        method: 'POST',
        body: JSON.stringify({
          messageId,
          feedbackType,
          feedbackText,
        }),
      });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get current session ID
   */
  getSessionId(): string | null {
    return this.sessionId;
  }

  /**
   * Set session ID (for resuming)
   */
  setSessionId(sessionId: string): void {
    this.sessionId = sessionId;
  }
}
